import React, { useState, useEffect } from 'react';
import { Box, Grid } from '@mui/material';
import { Button } from '@jcm/design-system';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import EnvioEmails from './EnvioEmails/EnvioEmails';
import GerenciarObrigacao from './GerenciarObrigacao/GerenciarObrigacao';
import CalculoPeriodicidade from './CalculoPeriodicidade/CalculoPeriodicidade';
import CustomHeader from './CustomHeaderForm/CustomHeader';
import ModalAlterarData from './Acoes/ModalAlterarData/ModalAlterarData';
import ModalConcluirObrigacao from '../../../components/obrigacoes/ModalConcluirObrigacao/ModalConcluirObrigacao';
import ModalCancelarObrigacao from './Acoes/ModalCancelarObrigacao/ModalCancelarObrigacao';
import LogsModal from './LogsModal/index';
import AcoesAnexo from './Acoes/AcoesAnexo';
import Anexos from './Anexos/Anexos';
import CustomAlert from '../../../../../../shared/components/customAlert/customAlert';

import ObrigacoesService from '../../../services/Obrigacoes/Obrigacoes.service';
import {
	obrigacaoEstaConcluida,
	usuarioEhAdmin,
	verificarAprovadorPrincipal,
	verificarResponsavelPrincipal,
} from '../../../utils/determinarAcessoObrigacoes';

import { createFormSchema } from '../../../zod/validations/formObrigacoesSchema';
import { CriarObrigacaoPayload, TObrigacao } from '../../../ts/types/ObrigacaoEtapa/Obrigacoes/Obrigacoes';
import { useGovernancaContext } from '../../../../../../context/GovernancaContext';
import { TipoDadoEnum } from '../../../ts/enums/TipoDadoEnum';
import { CriarAnexoPayload } from '../../../ts/types/ObrigacaoEtapa/common/Anexos';
import { EnumTipoAnexo } from '../../../ts/enums/TipoAnexoEnum';

interface FormObrigacoesProps {
	initialData?: Partial<CriarObrigacaoPayload>;
	onSave?: (data: CriarObrigacaoPayload) => void;
	onClose?: () => void;
	isEdit?: boolean;
	empresaId: number | string;
	headerTitle?: string;
	status?: string;
	empresaNome?: string;
	obrigacaoId?: number | string;
	onObrigacaoChange: (obrigacaoAtualizada: TObrigacao) => void;
	obrigacao?: TObrigacao;
	onAttachClick?: () => void;
	onAtualizar?: () => void;
	onAlteracao?: (alterado: boolean) => void;
}

const FormObrigacoes: React.FC<FormObrigacoesProps> = ({
	initialData = {},
	onClose,
	isEdit = false,
	empresaId,
	obrigacaoId,
	empresaNome,
	onObrigacaoChange,
	obrigacao,
	onAtualizar,
	onAlteracao,
}) => {
	const _obrigacoesService = new ObrigacoesService();
	const { usuario } = useGovernancaContext();

	const [isModalAlterarVencimento, setIsModalAlterarVencimento] = useState(false);
	const [isModalConluirObrigacao, setIsModalConcluirObrigacao] = useState(false);
	const [isModalCancelarObrigacao, setIsModalCancelarObrigacao] = useState(false);
	const [isModalLogs, setIsModalLogs] = useState(false);
	const [isModalAnexosVisible, setIsModalAnexosVisible] = useState(false);
	const [dadosCarregados, setDadosCarregados] = useState(false);
	const etapas = obrigacao?.children;

	const [anexoData, setAnexoData] = useState<CriarAnexoPayload>({
		arquivo: null,
		descricao: '',
	});
	const [precisaAtualizarListagem, setPrecisaAtualizarListagem] = useState(false);

	const [alert, setAlert] = useState<{
		type: 'success' | 'error';
		content: string;
		title?: string;
	} | null>(null);

	const [data, setData] = useState<CriarObrigacaoPayload>({
		nome: initialData.nome || '',
		aprovadorPrincipal: initialData.aprovadorPrincipal || 0,
		responsavelPrincipal: initialData.responsavelPrincipal || 0,
		aprovadoresSelecionados: initialData.aprovadoresSelecionados || [],
		responsaveisSelecionados: initialData.responsaveisSelecionados || [],
		acompanhamento: initialData.acompanhamento || '',
		fundamentacaoLegal: initialData.fundamentacaoLegal || '',
		datas: initialData.datas || '',
		prazoLegal: initialData.prazoLegal || '',
		temporalidade: initialData.temporalidade || '',
		ehLegal: initialData.ehLegal,
		formaCumprimento: initialData.formaCumprimento || '',
		listaEmailSelecionados: initialData.listaEmailSelecionados || [],
		listaEmailSomenteAtrasoSelecionados: initialData.listaEmailSomenteAtrasoSelecionados || [],
		origemSelecionada: initialData.origemSelecionada,

		//E-mail
		antecedenciaSelecionada: initialData.antecedenciaSelecionada,
		frequenciaVencidaSelecionada: initialData.frequenciaVencidaSelecionada,

		diaUtil: initialData.diaUtil,
		dataVencimento: initialData.dataVencimento,
		dataFinal: initialData.dataFinal,

		// Campos adicionais
		justificativaAtraso: initialData.justificativaAtraso || '',
		justificativaCancelar: initialData.justificativaCancelar || '',
		justificativaImplementacao: initialData.justificativaImplementacao || '',
		justificativaNaoAprovacao: initialData.justificativaNaoAprovacao || '',
		justificativaNaoAprovacaoDataAlterada: initialData.justificativaNaoAprovacaoDataAlterada || '',
		justificativaNaoAprovarCancelamento: initialData.justificativaNaoAprovarCancelamento || '',
		pontoMelhoria: initialData.pontoMelhoria || '',
		reaprovada: initialData.reaprovada || false,
		obrigacaoConcluida: initialData.obrigacaoConcluida || false,
		emAtraso: initialData.emAtraso || false,
		emAtrasoDataFinalSolicitada: initialData.emAtrasoDataFinalSolicitada || false,
		podeConcluir: initialData.podeConcluir || false,
		novaDataFinal: initialData.novaDataFinal,
		justificativaDataAlterada: initialData.justificativaDataAlterada || '',

		// Conclusao
		dataConclusaoObrigacao: new Date(),
		dataHoje: new Date(),
		estadoStatusAprovada: initialData.estadoStatusAprovada,
		estadoStatusCancelamento: initialData.estadoStatusCancelamento || 0,
		estadoStatusAlterarData: initialData.estadoStatusAlterarData || null,
	});
	const [loading, setLoading] = useState(false);

	const schema = createFormSchema(isEdit);
	type FormValues = z.infer<ReturnType<typeof createFormSchema>>;

	useEffect(() => {
		if (precisaAtualizarListagem) {
			onAlteracao?.(true);
		}
	}, [precisaAtualizarListagem]);

	useEffect(() => {
		setData({
			nome: initialData.nome || '',
			aprovadorPrincipal: initialData.aprovadorPrincipal || 0,
			responsavelPrincipal: initialData.responsavelPrincipal || 0,
			aprovadoresSelecionados: initialData.aprovadoresSelecionados || [],
			responsaveisSelecionados: initialData.responsaveisSelecionados || [],
			acompanhamento: initialData.acompanhamento || '',
			fundamentacaoLegal: initialData.fundamentacaoLegal || '',
			datas: initialData.datas || '',
			prazoLegal: initialData.prazoLegal || '',
			temporalidade: initialData.temporalidade || '',
			formaCumprimento: initialData.formaCumprimento || '',
			listaEmailSelecionados: initialData.listaEmailSelecionados || [],
			listaEmailSomenteAtrasoSelecionados: initialData.listaEmailSomenteAtrasoSelecionados || [],
			origemSelecionada: initialData.origemSelecionada,

			//E-mail
			antecedenciaSelecionada: initialData.antecedenciaSelecionada,
			frequenciaVencidaSelecionada: initialData.frequenciaVencidaSelecionada,

			ehLegal: initialData.ehLegal,
			diaUtil: initialData.diaUtil,
			dataVencimento: initialData.dataVencimento,
			dataFinal: initialData.dataFinal,

			// Campos adicionais
			justificativaAtraso: initialData.justificativaAtraso || '',
			justificativaCancelar: initialData.justificativaCancelar || '',
			justificativaImplementacao: initialData.justificativaImplementacao || '',
			justificativaNaoAprovacao: initialData.justificativaNaoAprovacao || '',
			justificativaNaoAprovacaoDataAlterada: initialData.justificativaNaoAprovacaoDataAlterada || '',
			justificativaNaoAprovarCancelamento: initialData.justificativaNaoAprovarCancelamento || '',
			pontoMelhoria: initialData.pontoMelhoria || '',
			reaprovada: initialData.reaprovada || false,
			obrigacaoConcluida: initialData.obrigacaoConcluida || false,
			emAtraso: initialData.emAtraso || false,
			emAtrasoDataFinalSolicitada: initialData.emAtrasoDataFinalSolicitada || false,
			podeConcluir: initialData.podeConcluir || false,
			dataConclusaoObrigacao: new Date(),
			dataHoje: new Date(),
			novaDataFinal: initialData.novaDataFinal,
			justificativaDataAlterada: initialData.justificativaDataAlterada || '',
			estadoStatusAprovada: initialData.estadoStatusAprovada,
			estadoStatusAlterarData: initialData.estadoStatusAlterarData || 0,
			estadoStatusCancelamento: initialData.estadoStatusCancelamento || 0,
		});
		setDadosCarregados(true);
	}, [initialData]);

	const handleOpenAlterarVencimentoModal = () => {
		if (dadosCarregados) {
			setIsModalAlterarVencimento(true);
		} else {
			setAlert({
				type: 'error',
				content: 'Aguarde o carregamento das informações antes de continuar.',
			});
		}
	};

	const handleOpenConcluirObrigacaoModal = () => {
		if (dadosCarregados) {
			setIsModalConcluirObrigacao(true);
		} else {
			setAlert({
				type: 'error',
				content: 'Aguarde o carregamento das informações antes de continuar.',
			});
		}
	};

	const handleOpenCancelarObrigacaoModal = () => {
		if (dadosCarregados) {
			setIsModalCancelarObrigacao(true);
		} else {
			setAlert({
				type: 'error',
				content: 'Aguarde o carregamento das informações antes de continuar.',
			});
		}
	};

	const handleCloseFormulario = () => {
		if (precisaAtualizarListagem && onAtualizar) {
			onAtualizar();
		}

		onClose?.();
	};

	const handleAnexoChange = (field: keyof CriarAnexoPayload, value: unknown) => {
		setAnexoData((prev) => ({ ...prev, [field]: value }));
	};

	const handleAttachClick = async () => {
		if (isModalAnexosVisible) return;

		setIsModalAnexosVisible(true);

		try {
			const anexosAtualizados = await _obrigacoesService.getListaAnexos(empresaId, obrigacaoId as number);
			setAnexoData((prevState) => ({
				...prevState,
				anexos: anexosAtualizados,
			}));
		} catch {
			setAlert({ type: 'error', content: 'Erro ao buscar anexos.' });
		}
	};

	const handleCloseAnexosModal = () => {
		if (precisaAtualizarListagem || anexoData.arquivo || anexoData.descricao) {
			setPrecisaAtualizarListagem(true);
			onAlteracao?.(true);
		}

		setIsModalAnexosVisible(false);
		setAnexoData({ arquivo: null, descricao: '' });
	};

	const createDefaultValues = (initialData: Partial<CriarObrigacaoPayload>): FormValues => {
		const values: any = {
			id: initialData.id ?? null,
			nome: initialData.nome ?? '',
			aprovadorPrincipal: initialData.aprovadorPrincipal ?? 0,
			responsavelPrincipal: initialData.responsavelPrincipal ?? 0,
			aprovadoresSelecionados: initialData.aprovadoresSelecionados ?? [],
			responsaveisSelecionados: initialData.responsaveisSelecionados ?? [],
			acompanhamento: initialData.acompanhamento ?? '',
			fundamentacaoLegal: initialData.fundamentacaoLegal ?? '',
			datas: initialData.datas ?? '',
			prazoLegal: initialData.prazoLegal ?? '',
			temporalidade: initialData.temporalidade ?? '',
			ehLegal: initialData.ehLegal,
			formaCumprimento: initialData.formaCumprimento ?? '',
			listaEmailSelecionados: initialData.listaEmailSelecionados ?? [],
			listaEmailSomenteAtrasoSelecionados: initialData.listaEmailSomenteAtrasoSelecionados ?? [],
			tipoVencimento: initialData.tipoVencimento ?? null,

			diaUtil: initialData.diaUtil,
			dataVencimento: initialData.dataVencimento ?? '',
			origemSelecionada: initialData.origemSelecionada,
			codigo: initialData.codigo,

			frequenciaSelecionada: initialData.frequenciaSelecionada ?? null,
			antecedenciaSelecionada: initialData.antecedenciaSelecionada ?? null,
			frequenciaVencidaSelecionada: initialData.frequenciaVencidaSelecionada ?? null,
			periodicidadeSelecionada: initialData.periodoSelecionado ?? null,

			// Campos adicionais
			justificativaAtraso: initialData.justificativaAtraso ?? '',
			justificativaCancelar: initialData.justificativaCancelar ?? '',
			justificativaImplementacao: initialData.justificativaImplementacao ?? '',
			justificativaNaoAprovacao: initialData.justificativaNaoAprovacao ?? '',
			justificativaNaoAprovacaoDataAlterada: initialData.justificativaNaoAprovacaoDataAlterada ?? '',
			justificativaNaoAprovarCancelamento: initialData.justificativaNaoAprovarCancelamento ?? '',
			pontoMelhoria: initialData.pontoMelhoria ?? '',
			reaprovada: initialData.reaprovada ?? false,
			obrigacaoConcluida: initialData.obrigacaoConcluida ?? false,
			emAtraso: initialData.emAtraso ?? false,
			emAtrasoDataFinalSolicitada: initialData.emAtrasoDataFinalSolicitada ?? false,
			podeConcluir: initialData.podeConcluir ?? false,
			dataConclusaoObrigacao: new Date(),
			dataHoje: new Date(),

			// Alteração data final
			observacao: initialData.observacao,
			dataFinalAnterior: initialData.dataVencimento,
			novaDataFinal: initialData.novaDataFinal,
			justificativaDataAlterada: initialData.justificativaDataAlterada || '',
			estadoStatusAprovada: initialData.estadoStatusAprovada,
			estadoStatusCancelamento: initialData.estadoStatusCancelamento || 0,
			estadoStatusAlterarData: initialData.estadoStatusAlterarData || 0,
			periodoSelecionado: initialData.periodoSelecionado,
			casoNaoUtilSelecionado: initialData.casoNaoUtilSelecionado || 2,
		};

		if (values.periodicidadeSelecionada === null && isEdit) {
			delete values.periodicidadeSelecionada;
		}

		return values;
	};

	const defaultValues = createDefaultValues(initialData);
	const ehAprovadorPrincipal = verificarAprovadorPrincipal(
		defaultValues.aprovadorPrincipal,
		defaultValues.aprovadoresSelecionados ?? [],
	);
	const ehResponsavelPrincipal = verificarResponsavelPrincipal(
		defaultValues.responsavelPrincipal,
		defaultValues.responsaveisSelecionados ?? [],
	);
	
	const {
		control,
		setValue,
		getValues,
		formState: { errors },
		handleSubmit,
		unregister,
	} = useForm<FormValues>({
		resolver: zodResolver(schema),
		defaultValues,
	});

	const handleAnexoSubmit = async () => {
		try {
			if (anexoData.arquivo && obrigacao?.id) {
				const res = await _obrigacoesService.criarAnexoObrigacao(
					empresaId,
					obrigacao.id,
					anexoData.arquivo,
					anexoData.descricao,
					EnumTipoAnexo.Obrigacao,
					'usuarioAtivo',
				);

				if (res.isSuccess) {
					setAlert({
						type: 'success',
						content: 'Anexo criado com sucesso.',
						title: 'Sucesso!',
					});

					setAnexoData({ arquivo: null, descricao: '' });
					setPrecisaAtualizarListagem(true);
					onAlteracao?.(true);
				} else {
					setAlert({ type: 'error', content: 'Erro ao enviar o anexo.' });
				}
			} else {
				setAlert({ type: 'error', content: 'Nenhum arquivo foi selecionado.' });
			}
		} catch {
			setAlert({
				type: 'error',
				content: 'Ocorreu um erro ao criar o anexo. Tente novamente mais tarde.',
			});
		}
	};

	const onSubmit = async (data: FormValues) => {
		setLoading(true);

		try {
			let response;
			if (!isEdit) {
				response = await _obrigacoesService.criarObrigacao(empresaId, data);
			} else if (isEdit && obrigacaoId) {
				response = await _obrigacoesService.atualizarObrigacao(empresaId, obrigacaoId, data as CriarObrigacaoPayload);
			}

			if (response?.data) {
				setAlert({
					type: 'success',
					content: 'Obrigação salva com sucesso!',
					title: 'Sucesso!',
				});

				onObrigacaoChange(response.data);

				if (onAtualizar) {
					onAtualizar();
				}

				setTimeout(() => {
					onClose?.();
				}, 1000);
			}
		} catch (error) {
			console.error('Erro ao salvar a obrigação:', error);
			setAlert({
				type: 'error',
				content: 'Erro ao salvar a obrigação. Tente novamente!',
				title: 'Erro',
			});
		} finally {
			setLoading(false);
		}
	};

	const handleChange = (field: string, value: unknown) => {
		setData({
			...data,
			[field]: value,
		});
	};

	return (
		<form
			onSubmit={(ev) => {
				ev.preventDefault();
			}}
		>
			<CustomHeader
				titulo='Obrigação'
				isEdit={isEdit}
				empresaNome={empresaNome || ''}
				dataVencimento={data.dataVencimento?.toString() || ''}
				obrigacao={initialData}
				isModalLogs={isModalLogs}
				openAlterarVencimentoModal={handleOpenAlterarVencimentoModal}
				openConcluirObrigacaoModal={handleOpenConcluirObrigacaoModal}
				openModalLogs={setIsModalLogs}
				usuarioAprovador={ehAprovadorPrincipal}
				usuarioResponsavel={ehResponsavelPrincipal}
				onAttachClick={handleAttachClick}
			/>

			<Box sx={{ padding: 2, margin: '30px 0 0 0' }}>
				<Grid container spacing={2}>
					<Grid item xs={12} sm={12} md={7} lg={6} xl={6}>
						<GerenciarObrigacao
							data={data}
							setValue={setValue}
							getValues={getValues}
							isEdit={isEdit}
							empresaId={empresaId}
							control={control}
							errors={errors}
							onChange={handleChange}
							usuarioAprovador={ehAprovadorPrincipal}
							usuarioResponsavel={ehResponsavelPrincipal}
						/>
					</Grid>
					<Grid item xs={12} sm={12} md={5} lg={6} xl={6}>
						<Grid container spacing={2}>
							{!isEdit && (
								<Grid item xs={12}>
									<CalculoPeriodicidade
										tipoDado={TipoDadoEnum.Obrigacao}
										control={control}
										errors={errors}
										setValue={setValue}
										getValues={getValues}
									/>
								</Grid>
							)}

							<Grid item xs={12}>
								<EnvioEmails
									control={control}
									errors={errors}
									empresaId={empresaId}
									setValue={setValue}
									getValues={getValues}
									usuarioAprovador={ehAprovadorPrincipal}
									usuarioResponsavel={ehResponsavelPrincipal}
									isEdit={isEdit}
								/>
								<LogsModal
									isModalLogs={isModalLogs}
									setIsModalLogs={setIsModalLogs}
									empresaId={empresaId}
									obrigacaoId={initialData.id}
								/>
							</Grid>

							{isEdit && (
								<Grid item xs={12}>
									<AcoesAnexo
										etapas={etapas}
										empresaId={empresaId}
										setValue={setValue}
										getValues={getValues}
										control={control}
										unregister={unregister}
										usuarioAprovador={ehAprovadorPrincipal}
										usuarioResponsavel={ehResponsavelPrincipal}
										aprovadoresSelecionados={data.aprovadoresSelecionados || []}
										responsaveisSelecionados={data.responsaveisSelecionados || []}
										openConcluirObrigacaoModal={handleOpenConcluirObrigacaoModal}
										openAlterarVencimentoModal={handleOpenAlterarVencimentoModal}
										openCancelarObrigacaoModal={handleOpenCancelarObrigacaoModal}
										data={data}
										onAttachClick={handleAttachClick}
										tipoDado={TipoDadoEnum.Obrigacao}
									/>
								</Grid>
							)}
						</Grid>
					</Grid>
				</Grid>

				<Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '1rem', gap: '20px' }}>
					<Button
						style={{ color: '#041F1F', backgroundColor: '#FFDAD6' }}
						type='filled-tonal'
						variant='error'
						onClick={(ev) => {
							ev.preventDefault();
							handleCloseFormulario();
						}}
						itemType='button'
						disabled={loading}
					>
						Cancelar
					</Button>

					<Button
						style={{
							backgroundColor: '#CCE8E7',
							color: '#041F1F',
						}}
						variant='save'
						loading={loading}
						disabled={
							obrigacaoEstaConcluida(data.estadoStatusAprovada ?? 0) ||
							(usuarioEhAdmin(usuario?.funcao, ehResponsavelPrincipal) && isEdit)
						}
						onClick={() => {
							handleSubmit(onSubmit)();
						}}
					>
						Salvar
					</Button>
				</Box>
			</Box>

			<ModalConcluirObrigacao
				isVisible={isModalConluirObrigacao && dadosCarregados}
				onClose={() => setIsModalConcluirObrigacao(false)}
				getValues={getValues}
				setValue={setValue}
				control={control}
				unregister={unregister}
				usuarioAprovador={ehAprovadorPrincipal}
				usuarioResponsavel={ehResponsavelPrincipal}
				handleSubmit={handleSubmit(onSubmit)}
			/>

			<ModalAlterarData
				isVisible={isModalAlterarVencimento && dadosCarregados}
				onClose={() => setIsModalAlterarVencimento(false)}
				getValues={getValues}
				setValue={setValue}
				control={control}
				unregister={unregister}
				usuarioAprovador={ehAprovadorPrincipal}
				usuarioResponsavel={ehResponsavelPrincipal}
				handleSubmit={handleSubmit(onSubmit)}
				tipoDado={TipoDadoEnum.Obrigacao}
				obrigacao={obrigacao}
			/>

			<ModalCancelarObrigacao
				isVisible={isModalCancelarObrigacao && dadosCarregados}
				onClose={() => setIsModalCancelarObrigacao(false)}
				getValues={getValues}
				setValue={setValue}
				control={control}
				unregister={unregister}
				usuarioAprovador={ehAprovadorPrincipal}
				usuarioResponsavel={ehResponsavelPrincipal}
				handleSubmit={handleSubmit(onSubmit)}
			/>

			<Anexos
				empresaId={Number(empresaId)}
				data={anexoData}
				obrigacao={obrigacao || ({} as TObrigacao)}
				onChange={handleAnexoChange}
				onSave={handleAnexoSubmit}
				isVisible={isModalAnexosVisible && dadosCarregados}
				onAtualizar={onAtualizar}
				onClose={handleCloseAnexosModal}
				origem='formulario'
				setPrecisaAtualizarListagem={setPrecisaAtualizarListagem}
				tipoDado={TipoDadoEnum.Obrigacao}
			/>

			{alert && <CustomAlert type={alert.type} content={alert.content} title={alert.title} />}
		</form>
	);
};

export default FormObrigacoes;
